import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type {
  AoSGameState,
  MoveGoodsArgs,
  GoodsColor
} from '../../types';
import { validateMoveGoodsBasic } from '../validators/moveGoodsValidator';
import { findShortestPath, calculateIncome } from '../utils/pathfinder';

/**
 * 商品輸送Move（基本ルール）
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - 商品輸送引数
 * @returns void or INVALID_MOVE
 *
 * @see docs/specifications/logic/common/phase_05_move_goods.md
 */
export function moveGoods(
  { G, ctx }: { G: AoSGameState; ctx: Ctx },
  args: MoveGoodsArgs
): void | typeof INVALID_MOVE {
  // 1. バリデーション（基本チェック）
  const basicValidation = validateMoveGoodsBasic(G, ctx, args);
  if (!basicValidation.success) {
    console.error(`[moveGoods] Validation failed: ${basicValidation.error} - ${basicValidation.message}`);
    return INVALID_MOVE;
  }

  // 2. 経路決定（UIから指定された場合はそれを使用、なければ自動計算）
  const player = G.players[ctx.currentPlayer];

  let path: string[] | null;

  if (args.path && args.path.length > 0) {
    // UIから経路が指定されている場合はそれを使用
    debug.log(`[moveGoods] Using provided path (${args.path.length} links): ${args.path.join(' → ')}`);
    // 経路の妥当性チェック（機関車レベル以内かどうか）
    if (args.path.length > player.locomotiveLevel) {
      console.error(`[moveGoods] Provided path exceeds locomotive level (${args.path.length} > ${player.locomotiveLevel})`);
      return INVALID_MOVE;
    }
    path = args.path;
  } else {
    // 経路が指定されていない場合は自動計算
    debug.log(`[moveGoods] DEBUG: finding path from ${args.fromCityId} to ${args.toCityId} (goodsColor=${args.goodsColor}, locoLevel=${player.locomotiveLevel})`);

    path = findShortestPath(
      G,
      args.fromCityId,
      args.toCityId,
      args.goodsColor,
      player.locomotiveLevel
    );

    if (!path) {
      // 経路が見つからない（到達不可、経路長超過、または同色都市回避不可）
      console.error(`[moveGoods] No valid path found from ${args.fromCityId} to ${args.toCityId}`);
      return INVALID_MOVE;
    }

    debug.log(`[moveGoods] Path auto-calculated (${path.length} links): ${path.join(' → ')}`);
  }

  // 3. 収入計算
  const income = calculateIncome(G, path);
  debug.log(`[moveGoods] Income calculated:`, income);

  // 4. 商品移動（袋に戻す）
  moveGoodsToBag(G, args.fromCityId, args.goodsColor);

  // 5. 収入更新
  for (const [playerId, amount] of Object.entries(income)) {
    const incomePlayer = G.players[playerId];
    if (incomePlayer && !incomePlayer.isBankrupt) {
      incomePlayer.incomeLevel += amount;
      debug.log(`[moveGoods] Player ${playerId} income +${amount} (total: ${incomePlayer.incomeLevel})`);
    }
  }

  // 6. 輸送回数カウント
  player.goodsTransportedThisPhase += 1;
  debug.log(`[moveGoods] Player ${ctx.currentPlayer} transported ${player.goodsTransportedThisPhase}/2 goods this phase`);

  // 7. 手番回数カウント
  player.turnsCompletedThisPhase += 1;
  debug.log(`[moveGoods] Player ${ctx.currentPlayer} completed turn ${player.turnsCompletedThisPhase}/2`);

  debug.log(`[moveGoods] Goods transported successfully`);
}

/**
 * 商品を輸送し、袋に戻す
 *
 * @param G - ゲーム状態
 * @param fromCityId - 出発都市ID（新都市タイルも含む）
 * @param goodsColor - 商品色
 */
function moveGoodsToBag(
  G: AoSGameState,
  fromLocationId: string,
  goodsColor: GoodsColor
): void {
  // 1. 出発地点のHexを取得（都市、町、または新都市タイル）
  const fromHex = G.map.find(hex =>
    (hex.city?.id === fromLocationId) ||
    (hex.town?.id === fromLocationId) ||
    (hex.town?.newCity?.id === fromLocationId)
  );

  if (!fromHex) {
    throw new Error(`Location ${fromLocationId} not found`);
  }

  // 2. 商品キューブを除去
  const goodsIndex = fromHex.goods.indexOf(goodsColor);
  if (goodsIndex === -1) {
    throw new Error(`Goods ${goodsColor} not found at ${fromLocationId}`);
  }

  fromHex.goods.splice(goodsIndex, 1);

  // 3. 袋に戻す
  G.cubesInBag.push(goodsColor);

  debug.log(`[moveGoodsToBag] ${goodsColor} goods moved from ${fromLocationId} to bag`);
}
